import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { getAuthHeaders, useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { UnmatchedExportButton } from '../components/sales/SalesCodeMatching';
import { useDebouncedValue } from '../components/sales/useAbortableGet';
import { canEditModule } from '../lib/permissions';
import { ImportPreviewDialog } from '../components/ImportPreviewDialog';
import { errorMessage } from '../components/LoadError';
import { Button } from '../components/ui/button';
import { StatStrip } from '../components/StatStrip';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Edit2, Search, Upload, Download, FileSpreadsheet, X, CheckCircle, AlertCircle, ChevronUp, ChevronDown, Filter, ChevronLeft, ChevronRight, AlertTriangle, Package, RefreshCw, TrendingUp, TrendingDown, ShoppingCart, Store, ArrowRightLeft, Lightbulb, Warehouse } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useNavigate } from 'react-router-dom';
import { INVENTORY_HEALTH, StockStatusBadge, StockLegend, formatDaysOfStock, daysOfStockTitle, daysSortValue, daysCoverClass } from '../lib/stockStatus';
import { platformLabel } from '../config/platforms';

import { useChannels } from '../hooks/useChannels';
const priorityClass = (isHigh, isMed, high, med, fallback) => {
  if (isHigh) return high;
  if (isMed) return med;
  return fallback;
};
const recommendationLabel = (action, isHigh) => {
  if (action === 'transfer_in') return isHigh ? 'REFILL' : 'LOW';
  return action === 'transfer_out' ? 'EXCESS' : 'REORDER';
};
const compareValues = (a, b) => {
  if (a > b) return 1;
  if (a < b) return -1;
  return 0;
};
const availableStockClass = stock => {
  if (stock <= 0) return 'text-red-700';
  return stock < 10 ? 'text-orange-700' : 'text-green-700';
};
// Cover cell: stocked-out SKUs read "0d" (red), "No sales" only when there is stock but no sales,
// and a slow mover whose cover is 999+ days reads "365+d" (backend sends days_cover=null for both).
const coverOptions = item => ({ available: item.available_stock, velocity: item.daily_avg_sales });
const coverTitle = item => daysOfStockTitle(item.days_cover, coverOptions(item));

const readFileAsBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = event => resolve(event.target?.result?.split(',')[1]);
  reader.onerror = () => reject(new Error('Failed to read file'));
  reader.readAsDataURL(file);
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ITEMS_PER_PAGE = 50;

// Platform colors for bin stock - new structure with Amazon Seller
const PLATFORM_COLORS = {
  warehouse: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Warehouse' },
  amazon_seller: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', label: 'Amazon Seller' },
  amazon_retail: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Amazon Vendor' },
  flipkart: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-300', label: 'Flipkart' },
  firstcry: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200', label: 'FirstCry' },
  myntra: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', label: 'Myntra' },
  d2c_drogo: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'D2C Drogo' },
  d2c_baybee: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'D2C Baybee' },
  d2c_domestica: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', label: 'D2C Domestica' },
};

// All bin keys in order
const BIN_KEYS = ['warehouse', 'amazon_seller', 'amazon_retail', 'flipkart', 'firstcry', 'myntra', 'd2c_drogo', 'd2c_baybee', 'd2c_domestica'];

// Bin Stock Cell Component
const BinStockCell = ({ value, platform, recommendation, onEdit }) => {
  const colors = PLATFORM_COLORS[platform] || PLATFORM_COLORS.warehouse;
  const hasRec = recommendation && recommendation.action;
  const isHigh = hasRec && recommendation.priority === 'high';
  const isMed = hasRec && recommendation.priority === 'medium';
  const ringClass = priorityClass(isHigh, isMed, 'ring-2 ring-red-400 animate-pulse', 'ring-2 ring-amber-400', '');
  const badgeClass = priorityClass(isHigh, isMed, 'bg-red-500 text-white', 'bg-amber-500 text-white', 'bg-green-100 text-green-700');
  
  return (
    <div className="flex flex-col items-center gap-0.5 relative group">
      <button
        type="button"
        onClick={onEdit}
        className={`px-2.5 py-1.5 rounded-md text-[11px] font-mono font-semibold ${colors.bg} ${colors.text} hover:opacity-80 transition-all min-w-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${ringClass}`}
        title={recommendation?.message || `Edit ${colors.label || platform} bin stock`}
        aria-label={`${colors.label || platform}: ${value || 0} units. Edit bin stock${hasRec ? `. ${recommendation.message}` : ''}`}
      >
        {value || 0}
      </button>
      {hasRec && (
        <>
          <span className={`text-[8px] font-bold uppercase tracking-wide px-1.5 py-0 rounded-sm leading-relaxed ${badgeClass}`}>
            {recommendationLabel(recommendation.action, isHigh)}
          </span>
          {/* Tooltip on hover */}
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block group-focus-within:block z-50 pointer-events-none" aria-hidden="true">
            <div className="bg-gray-900 text-white text-[10px] px-3 py-2 rounded-lg whitespace-nowrap max-w-[220px] text-center shadow-xl">
              <p className="font-medium">{recommendation.message}</p>
              {recommendation.suggested_qty > 0 && (
                <p className="text-blue-300 mt-0.5">Suggested: {recommendation.suggested_qty} units</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Quick Action Indicators Component
const ActionIndicators = ({ item, onNavigate }) => {
  const indicators = [];
  
  if (item.health === 'CRITICAL') {
    indicators.push(
      <button 
        key="critical" 
        onClick={() => onNavigate('/reorder')}
        className="flex items-center gap-1 px-1.5 py-0.5 bg-red-50 text-red-700 rounded text-[10px] font-medium hover:bg-red-100 transition-colors"
        title="Out of stock - open Reorder"
      >
        <AlertTriangle className="h-3 w-3" /> Reorder
      </button>
    );
  } else if (item.health === 'LOW') {
    indicators.push(
      <span key="low" className="flex items-center gap-1 px-1.5 py-0.5 bg-orange-50 text-orange-700 rounded text-[10px] font-medium" title="Low cover (7 days or less) - plan a reorder">
        <AlertCircle className="h-3 w-3" /> Low cover
      </span>
    );
  } else if (item.health === 'OVERSTOCK') {
    indicators.push(
      <span key="overstock" className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px] font-medium" title="Overstock (more than 90 days of cover) - hold">
        <Package className="h-3 w-3" /> Hold
      </span>
    );
  }
  
  if (item.allocation_risk) {
    indicators.push(
      <button 
        key="allocation" 
        onClick={() => onNavigate('/shipment-planning')}
        className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-medium hover:bg-blue-100 transition-colors"
        title="Retail Heavy - Allocation Risk"
      >
        <RefreshCw className="h-3 w-3" /> Allocate
      </button>
    );
  }
  
  return indicators.length > 0 ? (
    <div className="flex flex-wrap gap-1">{indicators}</div>
  ) : null;
};

// Channel Split Expandable Row Component
const ChannelSplitRow = ({ item }) => {
  const platformBreakdown = item.platform_breakdown || {};
  const platforms = Object.entries(platformBreakdown).filter(([_, v]) => v > 0);
  
  return (
    <tr className="bg-slate-50 border-b border-slate-100">
      <td colSpan="12" className="px-4 py-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Channel Split */}
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <h4 className="text-[11px] font-semibold text-gray-500 uppercase mb-2">Channel Split</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-[12px] text-gray-600">
                  <ShoppingCart className="h-3.5 w-3.5 text-blue-500" /> Marketplace
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-semibold">{item.marketplace_sold || 0}</span>
                  <span className="text-[11px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{item.marketplace_pct || 0}%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-[12px] text-gray-600">
                  <Store className="h-3.5 w-3.5 text-green-500" /> Retail
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-semibold">{item.retail_sold || 0}</span>
                  <span className="text-[11px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded">{item.retail_pct || 0}%</span>
                </div>
              </div>
              {/* Visual bar */}
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex mt-1">
                <div className="bg-blue-500 h-full" style={{ width: `${item.marketplace_pct || 0}%` }} />
                <div className="bg-green-500 h-full" style={{ width: `${item.retail_pct || 0}%` }} />
              </div>
            </div>
          </div>
          
          {/* Platform Breakdown */}
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <h4 className="text-[11px] font-semibold text-gray-500 uppercase mb-2">Platform Breakdown</h4>
            {platforms.length > 0 ? (
              <div className="space-y-1">
                {platforms.map(([platform, units]) => (
                  <div key={platform} className="flex items-center justify-between text-[12px]">
                    <span className="text-gray-600">{platformLabel(platform)}</span>
                    <span className="font-medium">{units} units</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[12px] text-gray-500">No sales data</p>
            )}
          </div>
          
          {/* Allocation Status */}
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <h4 className="text-[11px] font-semibold text-gray-500 uppercase mb-2">Allocation Status</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[12px]">
                <span className="text-gray-600">Pending Retail</span>
                <span className={`font-semibold ${item.pending_retail > 0 ? 'text-orange-600' : 'text-gray-500'}`}>
                  {item.pending_retail || 0} units
                </span>
              </div>
              <div className="flex items-center justify-between text-[12px]">
                <span className="text-gray-600">Available After</span>
                <span className={`font-semibold ${(item.available_stock - item.pending_retail) <= 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {Math.max(0, (item.available_stock || 0) - (item.pending_retail || 0))} units
                </span>
              </div>
              {item.allocation_risk && (
                <div className="mt-2 p-2 bg-orange-50 rounded text-[11px] text-orange-700">
                  <AlertTriangle className="h-3 w-3 inline mr-1" />
                  High retail allocation - marketplace risk
                </div>
              )}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
};

// Result of a bulk upload. Saved rows plus some skipped rows is a partial
// success (amber), not a failure; only "nothing saved" is red.
const UploadResultPanel = ({ result, source }) => {
  const errorCount = result.errors?.length || 0;
  const tone = errorCount === 0 ? 'ok' : (result.updated_count > 0 ? 'partial' : 'failed');
  const styles = {
    ok: { box: 'bg-green-50', text: 'text-green-800', Icon: CheckCircle, icon: 'text-green-600' },
    partial: { box: 'bg-amber-50', text: 'text-amber-800', Icon: AlertCircle, icon: 'text-amber-600' },
    failed: { box: 'bg-red-50', text: 'text-red-800', Icon: AlertCircle, icon: 'text-red-600' },
  }[tone];
  return (
    <div role={tone === 'ok' ? 'status' : 'alert'} className={`p-3 rounded-lg ${styles.box}`}>
      <div className="flex items-start gap-2">
        <styles.Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${styles.icon}`} aria-hidden="true" />
        <div className="flex-1">
          {result.message && <p className={`text-[13px] font-medium ${styles.text}`}>{result.message}</p>}
          {errorCount > 0 && (
            <>
              <p className={`text-[12px] mt-1 ${styles.text}`}>
                {errorCount} {errorCount === 1 ? 'row was' : 'rows were'} skipped:
              </p>
              <ul className="mt-1 space-y-1">
                {result.errors.slice(0, 5).map((error) => (
                  <li key={error} className={`text-[11px] ${styles.text}`}>{error}</li>
                ))}
                {errorCount > 5 && <li className={`text-[11px] ${styles.text}`}>...and {errorCount - 5} more</li>}
              </ul>
            </>
          )}
          {result.unmatched_codes?.length > 0 && <div className="mt-2"><UnmatchedExportButton codes={result.unmatched_codes} source={source} /></div>}
        </div>
      </div>
    </div>
  );
};

export const InventoryPage = () => {
  useChannels();   // re-render when channel names or colours change (Settings → Channels)
  const { user } = useAuth();
  const { country, currency } = useCountry();
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const fileInputRef = useRef(null);
  
  // Bin Stock Modal States
  const [binStockModalOpen, setBinStockModalOpen] = useState(false);
  const [binStockEditItem, setBinStockEditItem] = useState(null);
  const [binStockForm, setBinStockForm] = useState({
    warehouse: 0, amazon_seller: 0, amazon_retail: 0, flipkart: 0, firstcry: 0, myntra: 0, d2c_drogo: 0, d2c_baybee: 0, d2c_domestica: 0
  });
  
  // Bin Stock Bulk Upload States
  const [binStockBulkOpen, setBinStockBulkOpen] = useState(false);
  const [binStockUploading, setBinStockUploading] = useState(false);
  const [binStockUploadResult, setBinStockUploadResult] = useState(null);
  const binStockFileInputRef = useRef(null);
  // Amazon Vendor (amazon_retail) can only change via PO dispatch once it holds stock
  const amazonVendorLocked = (binStockEditItem?.bin_stock?.amazon_retail || binStockEditItem?.bin_stock?.amazon || 0) > 0;

  // Excel import preview (dry run) shared by Bulk Inventory Update and Bin Stock Import
  const [importPreview, setImportPreview] = useState(null);
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [bulkDragActive, setBulkDragActive] = useState(false);
  
  // Transfer Modal States
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({
    sku_code: '', from_bin: 'warehouse', to_bin: 'amazon_seller', quantity: 0
  });
  const [transferItem, setTransferItem] = useState(null);
  const [transferring, setTransferring] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [salesPeriod, setSalesPeriod] = useState('30');
  // Filter the (large) list only after typing pauses; the input itself stays bound to searchTerm.
  const debouncedSearch = useDebouncedValue(searchTerm, 250);
  const [brandFilter, setBrandFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [stockFilter, setStockFilter] = useState('ALL');
  const [healthFilter, setHealthFilter] = useState('ALL');
  const [showRecs, setShowRecs] = useState(false);
  const [reorderFilter, setReorderFilter] = useState(false);
  const [allocationRiskFilter, setAllocationRiskFilter] = useState(false);
  
  // Sorting
  const [sortField, setSortField] = useState('sku_code');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  
  const [formData, setFormData] = useState({
    sku_code: '',
    opening_stock: 0,
    incoming_stock: 0,
    adjustment: 0
  });

  // Warehouse staff may edit inventory (backend WAREHOUSE_WRITE_ALLOWED_PREFIXES), bulk uploads included.
  const canBulkUpdate = canEditModule(user, 'inventory');
  
  // Toggle row expansion for channel split
  const toggleRowExpand = (skuCode) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(skuCode)) {
        next.delete(skuCode);
      } else {
        next.add(skuCode);
      }
      return next;
    });
  };

  // Only the latest request may update state: switching the sales period or country quickly
  // (or a refresh after an edit) aborts the previous request so a slow, stale response can't win.
  const inventoryRequestRef = useRef(null);
  const fetchInventory = useCallback(async (days = salesPeriod) => {
    inventoryRequestRef.current?.abort();
    const controller = new AbortController();
    inventoryRequestRef.current = controller;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/inventory?days=${days}&country=${country}`, { headers: getAuthHeaders(), signal: controller.signal });
      if (inventoryRequestRef.current !== controller) return;
      setInventory(response.data);
    } catch (error) {
      if (controller.signal.aborted || inventoryRequestRef.current !== controller) return;
      toast.error('Failed to load inventory');
    } finally {
      if (inventoryRequestRef.current === controller) setLoading(false);
    }
  }, [country, salesPeriod]);

  useEffect(() => {
    fetchInventory(salesPeriod);
  }, [fetchInventory, salesPeriod]);

  // Abort any in-flight inventory request when the page unmounts.
  useEffect(() => () => inventoryRequestRef.current?.abort(), []);

  // Get unique brands and categories for filters
  const { brands, categories } = useMemo(() => {
    const brandSet = new Set();
    const categorySet = new Set();
    inventory.forEach(item => {
      if (item.brand) brandSet.add(item.brand);
      if (item.category) categorySet.add(item.category);
    });
    return {
      brands: Array.from(brandSet).sort(),
      categories: Array.from(categorySet).sort()
    };
  }, [inventory]);

  // Filtered and sorted inventory
  const filteredInventory = useMemo(() => {
    const searchNeedle = debouncedSearch.toLowerCase();
    let filtered = inventory.filter(item => {
      // Search filter
      const matchesSearch = 
        item.sku_code?.toLowerCase().includes(searchNeedle) ||
        item.product_name?.toLowerCase().includes(searchNeedle);
      
      // Brand filter
      const matchesBrand = brandFilter === 'ALL' || item.brand === brandFilter;
      
      // Category filter
      const matchesCategory = categoryFilter === 'ALL' || item.category === categoryFilter;
      
      // Stock status filter
      let matchesStock = true;
      if (stockFilter === 'CRITICAL') {
        matchesStock = item.available_stock <= 0;
      } else if (stockFilter === 'LOW') {
        matchesStock = item.available_stock > 0 && item.available_stock < 10;
      } else if (stockFilter === 'HEALTHY') {
        matchesStock = item.available_stock >= 10;
      }
      
      // Health filter (NEW)
      let matchesHealth = true;
      if (healthFilter !== 'ALL') {
        matchesHealth = item.health === healthFilter;
      }
      
      // Reorder filter
      const matchesReorder = !reorderFilter || item.needs_reorder;
      
      // Allocation risk filter
      const matchesAllocationRisk = !allocationRiskFilter || item.allocation_risk;
      
      return matchesSearch && matchesBrand && matchesCategory && matchesStock && matchesHealth && matchesReorder && matchesAllocationRisk;
    });
    
    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle numeric fields
      if (sortField === 'days_cover') {
        // Out of stock sorts as 0 days, "no sales" sorts after every real number.
        aVal = daysSortValue(a.days_cover, a.available_stock, a.daily_avg_sales);
        bVal = daysSortValue(b.days_cover, b.available_stock, b.daily_avg_sales);
      } else if (['opening_stock', 'incoming_stock', 'adjustment', 'total_sold', 'available_stock', 'daily_avg_sales'].includes(sortField)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }
      
      return sortDirection === 'asc' ? compareValues(aVal, bVal) : compareValues(bVal, aVal);
    });
    
    return filtered;
  }, [inventory, debouncedSearch, brandFilter, categoryFilter, stockFilter, healthFilter, reorderFilter, allocationRiskFilter, sortField, sortDirection]);

  // Paginated inventory
  const paginatedInventory = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredInventory.slice(startIndex, endIndex);
  }, [filteredInventory, currentPage]);

  const totalPages = Math.ceil(filteredInventory.length / ITEMS_PER_PAGE);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, brandFilter, categoryFilter, stockFilter, healthFilter, reorderFilter, allocationRiskFilter]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp className="h-3 w-3 inline ml-1" /> : 
      <ChevronDown className="h-3 w-3 inline ml-1" />;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/inventory/${encodeURIComponent(formData.sku_code)}?country=${encodeURIComponent(country)}`, formData, { headers: getAuthHeaders() });
      toast.success('Inventory updated successfully');
      setDialogOpen(false);
      fetchInventory();
    } catch (error) {
      toast.error('Failed to update inventory');
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      sku_code: item.sku_code,
      opening_stock: item.opening_stock,
      incoming_stock: item.incoming_stock,
      adjustment: item.adjustment
    });
    setDialogOpen(true);
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/inventory/bulk-template?country=${country}`, { headers: getAuthHeaders() });
      const link = document.createElement('a');
      link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data.file}`;
      link.download = response.data.filename;
      link.click();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Excel imports: read file -> dry run -> preview -> confirm re-sends the same file for real
  const importConfig = {
    bulk: { endpoint: 'bulk-update', setBusy: setUploading, setResult: setUploadResult, title: 'Review inventory update', entityLabel: 'SKUs' },
    bin: { endpoint: 'bin-stock-bulk-update', setBusy: setBinStockUploading, setResult: setBinStockUploadResult, title: 'Review bin stock import', entityLabel: 'SKUs' },
  };

  const startImportPreview = async (kind, file) => {
    if (!file) return;
    const cfg = importConfig[kind];
    if (!/\.xlsx?$/i.test(file.name || '')) {
      toast.error('Please choose an Excel file (.xlsx or .xls)');
      return;
    }
    cfg.setBusy(true);
    cfg.setResult(null);
    try {
      const base64 = await readFileAsBase64(file);
      const response = await axios.post(
        `${API}/inventory/${cfg.endpoint}?dry_run=true`,
        { file: base64, country },
        { headers: getAuthHeaders() }
      );
      setImportPreview({ kind, fileName: file.name, base64, preview: response.data });
      setImportPreviewOpen(true);
    } catch (error) {
      const message = errorMessage(error, 'Upload failed');
      toast.error(message);
      cfg.setResult({ errors: [message] });
    } finally {
      cfg.setBusy(false);
    }
  };

  const confirmImport = async () => {
    if (!importPreview) return;
    const cfg = importConfig[importPreview.kind];
    cfg.setBusy(true);
    try {
      const response = await axios.post(
        `${API}/inventory/${cfg.endpoint}`,
        { file: importPreview.base64, country },
        { headers: getAuthHeaders() }
      );
      cfg.setResult(response.data);
      toast.success(response.data.message);
      fetchInventory();
    } catch (error) {
      toast.error(errorMessage(error, 'Import failed'));
      throw error; // keep the preview open
    } finally {
      cfg.setBusy(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    startImportPreview('bulk', file);
  };

  const handleBulkDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setBulkDragActive(true);
  };

  const handleBulkDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Ignore leave events fired when moving over child elements
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setBulkDragActive(false);
  };

  const handleBulkDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBulkDragActive(false);
    if (uploading) return;
    startImportPreview('bulk', e.dataTransfer?.files?.[0]);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setBrandFilter('ALL');
    setCategoryFilter('ALL');
    setStockFilter('ALL');
    setHealthFilter('ALL');
    setReorderFilter(false);
    setAllocationRiskFilter(false);
  };

  const hasActiveFilters = searchTerm || brandFilter !== 'ALL' || categoryFilter !== 'ALL' || stockFilter !== 'ALL' || healthFilter !== 'ALL' || reorderFilter || allocationRiskFilter;
  
  // Bin Stock Functions
  const openBinStockModal = (item, e) => {
    e?.stopPropagation();
    setBinStockEditItem(item);
    setBinStockForm({
      warehouse: item.bin_stock?.warehouse || 0,
      amazon_retail: item.bin_stock?.amazon_retail || item.bin_stock?.amazon || 0,
      flipkart: item.bin_stock?.flipkart || 0,
      amazon_seller: item.bin_stock?.amazon_seller || 0,
      myntra: item.bin_stock?.myntra || 0,
      firstcry: item.bin_stock?.firstcry || 0,
      d2c_drogo: item.bin_stock?.d2c_drogo || 0,
      d2c_baybee: item.bin_stock?.d2c_baybee || 0,
      d2c_domestica: item.bin_stock?.d2c_domestica || 0
    });
    setBinStockModalOpen(true);
  };

  const handleBinStockSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/inventory/${encodeURIComponent(binStockEditItem.sku_code)}/bin-stock?country=${encodeURIComponent(country)}`,
        { bin_stock: binStockForm },
        { headers: getAuthHeaders() }
      );
      toast.success('Bin stock updated successfully');
      setBinStockModalOpen(false);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update bin stock');
      // 409: stock moved while editing; reload so the editor shows current numbers
      if (error.response?.status === 409) fetchInventory();
    }
  };

  // preset (optional): { from_bin, to_bin, quantity } from a smart recommendation.
  const openTransferModal = (item, e, preset = {}) => {
    e?.stopPropagation();
    const from = preset.from_bin || 'warehouse';
    const available = Number(item.bin_stock?.[from] || 0);
    setTransferItem(item);
    setTransferForm({
      sku_code: item.sku_code,
      from_bin: from,
      to_bin: preset.to_bin || (from === 'amazon_seller' ? 'warehouse' : 'amazon_seller'),
      quantity: preset.quantity ? Math.min(Number(preset.quantity) || 0, available) : 0
    });
    setTransferModalOpen(true);
  };

  // Open the transfer dialog pre-filled from a recommendation card.
  const openRecommendation = (a) => {
    const item = inventory.find(i => i.sku_code === a.sku);
    if (!item) return;
    if (a.action === 'transfer_out') openTransferModal(item, null, { from_bin: a.bin, to_bin: 'warehouse', quantity: a.qty });
    else openTransferModal(item, null, { from_bin: a.fromBin || 'warehouse', to_bin: a.bin, quantity: a.qty });
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (transferring) return;
    if (transferForm.quantity <= 0) {
      toast.error('Transfer quantity must be greater than 0');
      return;
    }
    setTransferring(true);
    try {
      const response = await axios.post(
        `${API}/inventory/transfer?country=${country}`,
        transferForm,
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message);
      setTransferModalOpen(false);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  // Bin Stock Bulk Upload Functions
  const downloadBinStockTemplate = async () => {
    try {
      const response = await axios.get(`${API}/inventory/bin-stock-template?country=${country}`, { headers: getAuthHeaders() });
      const link = document.createElement('a');
      link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data.file}`;
      link.download = response.data.filename;
      link.click();
      toast.success('Bin stock template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleBinStockFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (binStockFileInputRef.current) {
      binStockFileInputRef.current.value = '';
    }
    startImportPreview('bin', file);
  };
  
  // Summary stats
  const summaryStats = useMemo(() => {
    const critical = inventory.filter(i => i.health === 'CRITICAL').length;
    const low = inventory.filter(i => i.health === 'LOW').length;
    const overstock = inventory.filter(i => i.health === 'OVERSTOCK').length;
    const needsReorder = inventory.filter(i => i.needs_reorder).length;
    const allocationRisk = inventory.filter(i => i.allocation_risk).length;
    return { critical, low, overstock, needsReorder, allocationRisk };
  }, [inventory]);

  // Compute recommendation insights
  const recInsights = useMemo(() => {
    let highCount = 0;
    let medCount = 0;
    const topActions = [];

    for (const item of inventory) {
      const recs = item.bin_recommendations || {};
      for (const [bin, rec] of Object.entries(recs)) {
        if (rec.priority === 'high') {
          highCount++;
          topActions.push({
            sku: item.sku_code,
            product: item.product_name,
            bin,
            action: rec.action,
            fromBin: rec.from_bin,
            message: rec.message,
            qty: rec.suggested_qty,
            daysCover: rec.days_cover,
            priority: rec.priority,
          });
        } else if (rec.priority === 'medium') {
          medCount++;
        }
      }
    }
    return { highCount, medCount, topActions, total: highCount + medCount };
  }, [inventory]);

  // Transfer dialog: units in the source bin and why Transfer is disabled (shown under Quantity).
  const transferSourceQty = Number(transferItem?.bin_stock?.[transferForm.from_bin] || 0);
  const binName = key => PLATFORM_COLORS[key]?.label || key;
  let transferBlocker = '';
  if (transferForm.from_bin === transferForm.to_bin) transferBlocker = 'Pick two different bins.';
  else if (transferSourceQty <= 0) transferBlocker = `${binName(transferForm.from_bin)} has no stock to move.`;
  else if (transferForm.quantity > transferSourceQty) transferBlocker = `Only ${transferSourceQty.toLocaleString('en-IN')} units are in ${binName(transferForm.from_bin)}.`;
  else if (transferForm.quantity <= 0) transferBlocker = 'Enter how many units to move.';

  return (
    <div className="max-w-[1600px] mx-auto space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Inventory</h1>
          <p className="mt-1 text-[13px] text-gray-500">
            Track stock levels and availability • {filteredInventory.length} of {inventory.length} SKUs
          </p>
        </div>
        {canBulkUpdate && (
          <div className="flex flex-wrap items-center gap-2">
            <Button 
              onClick={() => setBulkUploadOpen(true)}
              variant="outline"
              className="h-9 px-4 text-[13px] font-medium rounded-lg"
              data-testid="bulk-upload-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Bulk Update
            </Button>
            <Button 
              onClick={() => setBinStockBulkOpen(true)}
              className="h-9 px-4 bg-[#007AFF] hover:bg-[#0066CC] text-white text-[13px] font-medium rounded-lg"
              data-testid="bin-stock-bulk-btn"
            >
              <Warehouse className="h-4 w-4 mr-2" />
              Bin Stock Import
            </Button>
          </div>
        )}
      </div>

      {/* Summary: one compact row of filters (the table starts in the first screen) */}
      <StatStrip testId="inventory-summary" items={[
        { key: 'critical', label: INVENTORY_HEALTH.CRITICAL.label, value: summaryStats.critical, tone: 'red', hint: INVENTORY_HEALTH.CRITICAL.rule,
          active: healthFilter === 'CRITICAL', onClick: () => setHealthFilter(healthFilter === 'CRITICAL' ? 'ALL' : 'CRITICAL') },
        { key: 'low', label: `${INVENTORY_HEALTH.LOW.label} (≤7 days)`, value: summaryStats.low, tone: 'orange', hint: INVENTORY_HEALTH.LOW.rule,
          active: healthFilter === 'LOW', onClick: () => setHealthFilter(healthFilter === 'LOW' ? 'ALL' : 'LOW') },
        { key: 'overstock', label: `${INVENTORY_HEALTH.OVERSTOCK.label} (>90 days)`, value: summaryStats.overstock, tone: 'purple', hint: INVENTORY_HEALTH.OVERSTOCK.rule,
          active: healthFilter === 'OVERSTOCK', onClick: () => setHealthFilter(healthFilter === 'OVERSTOCK' ? 'ALL' : 'OVERSTOCK') },
        { key: 'reorder', label: 'Need reorder', value: summaryStats.needsReorder, tone: 'blue', hint: "Stock + incoming is below the required stock for this SKU's channel",
          active: reorderFilter, onClick: () => setReorderFilter(!reorderFilter) },
        { key: 'allocation', label: 'Allocation risk', value: summaryStats.allocationRisk, tone: 'amber', hint: 'Open retail PO units are more than half of the available stock',
          active: allocationRiskFilter, onClick: () => setAllocationRiskFilter(!allocationRiskFilter) },
      ]} />

      <StockLegend map={INVENTORY_HEALTH} title="What do the health statuses mean?" testId="inventory-health-legend" />

      {/* Smart Recommendations Panel */}
      {recInsights.total > 0 && (
        <Card className="p-4 bg-gradient-to-r from-blue-50 via-white to-amber-50 rounded-2xl border border-blue-100 shadow-sm" data-testid="recommendations-panel">
          <div className={`flex items-center justify-between ${showRecs ? 'mb-3' : ''}`}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <Lightbulb className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-[13px] font-semibold text-gray-900">Smart Bin Recommendations</h3>
                <p className="text-[11px] text-gray-500">
                  {recInsights.highCount > 0 && <span className="text-red-600 font-semibold">{recInsights.highCount} urgent</span>}
                  {recInsights.highCount > 0 && recInsights.medCount > 0 && ' · '}
                  {recInsights.medCount > 0 && <span className="text-amber-600 font-medium">{recInsights.medCount} suggested</span>}
                  {' transfers based on sales velocity'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button type="button" variant="outline" size="sm" className="h-7 text-[12px]" onClick={() => setShowRecs(v => !v)}
                aria-expanded={showRecs} data-testid="recommendations-toggle">
                {showRecs ? 'Hide' : `Show ${recInsights.topActions.length}`}
              </Button>
              {recInsights.highCount > 0 && (
                <span className="text-[10px] font-bold bg-red-500 text-white px-2 py-0.5 rounded-full ">{recInsights.highCount} URGENT</span>
              )}
              {recInsights.medCount > 0 && (
                <span className="text-[10px] font-bold bg-amber-500 text-white px-2 py-0.5 rounded-full">{recInsights.medCount} WATCH</span>
              )}
            </div>
          </div>
          {showRecs && recInsights.topActions.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 max-h-[280px] overflow-y-auto">
              {recInsights.topActions.map((a, i) => (
                <button type="button" key={`${a.sku}-${a.bin}`} onClick={() => openRecommendation(a)} disabled={!canBulkUpdate || a.action === 'reorder'}
                  title={a.message}
                  className="px-3 py-2.5 text-left bg-white border border-red-200 rounded-xl hover:shadow-md transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-default disabled:hover:shadow-none" data-testid={`rec-card-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-mono text-gray-500">{a.sku}</span>
                    <span className="text-[9px] font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded">{a.daysCover != null ? `${a.daysCover}d cover` : 'No sales'}</span>
                  </div>
                  <p className="text-[11px] font-medium text-gray-900 truncate">{a.product}</p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <ArrowRightLeft className="h-3 w-3 text-blue-500 flex-shrink-0" />
                    <span className="text-[10px] text-gray-600">
                      {a.action === 'transfer_out' ? 'Move out of ' : a.action === 'reorder' ? 'Reorder for ' : 'Refill '}
                      <span className="font-semibold text-blue-700">{PLATFORM_COLORS[a.bin]?.label || a.bin}</span>{' '}
                      {a.action === 'transfer_out' ? `−${a.qty}` : `+${a.qty}`}
                      {a.action === 'transfer_in' && a.fromBin && a.fromBin !== 'warehouse' ? ` from ${PLATFORM_COLORS[a.fromBin]?.label || a.fromBin}` : ''}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>
      )}
      
      {/* Filters */}
      <Card className="p-4 xl:p-5 bg-white rounded-2xl border-0 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-md">
            <Search className="h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search SKU or product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-0 bg-gray-50 rounded-lg h-9"
              data-testid="search-input"
            />
          </div>
          
          {/* Sales Period */}
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-medium text-gray-500">Sold in:</span>
            <Select value={salesPeriod} onValueChange={setSalesPeriod}>
              <SelectTrigger className="w-[120px] h-9 rounded-lg bg-gray-50 border-0" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Brand Filter */}
          <Select value={brandFilter} onValueChange={setBrandFilter}>
            <SelectTrigger className="w-[140px] h-9 rounded-lg bg-gray-50 border-0" data-testid="brand-filter">
              <SelectValue placeholder="Brand" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Brands</SelectItem>
              {brands.map(brand => (
                <SelectItem key={brand} value={brand}>{brand}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Category Filter */}
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[160px] h-9 rounded-lg bg-gray-50 border-0" data-testid="category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Stock Status Filter */}
          <Select value={stockFilter} onValueChange={setStockFilter}>
            <SelectTrigger className="w-[160px] h-9 rounded-lg bg-gray-50 border-0" data-testid="stock-filter" aria-label="Units on hand" title="Filter by units on hand (not by days of cover)">
              <SelectValue placeholder="Units on hand" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Any units on hand</SelectItem>
              <SelectItem value="CRITICAL">0 units (out of stock)</SelectItem>
              <SelectItem value="LOW">1-9 units</SelectItem>
              <SelectItem value="HEALTHY">10+ units</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearFilters}
              className="h-9 text-[13px] text-gray-500 hover:text-gray-700"
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </Card>

      {/* Inventory Table */}
      <Card className="bg-white rounded-2xl border-0 shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="inventory-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-8 p-3"></th>
                  <th 
                    className="text-left p-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('sku_code')}
                  >
                    SKU {renderSortIcon('sku_code')}
                  </th>
                  <th 
                    className="text-right p-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('available_stock')}
                  >
                    Total {renderSortIcon('available_stock')}
                  </th>
                  {/* Bin Stock Columns - India Platforms */}
                  <th className="text-center p-2 text-[9px] font-semibold text-blue-600 uppercase tracking-wide bg-blue-50/50">
                    Warehouse
                  </th>
                  <th className="text-center p-2 text-[9px] font-semibold text-orange-600 uppercase tracking-wide bg-orange-50/50">
                    Amz Seller
                  </th>
                  <th className="text-center p-2 text-[9px] font-semibold text-amber-600 uppercase tracking-wide bg-amber-50/50">
                    Amz Vendor
                  </th>
                  <th className="text-center p-2 text-[9px] font-semibold text-blue-500 uppercase tracking-wide bg-blue-100/50">
                    Flipkart
                  </th>
                  <th className="text-center p-2 text-[9px] font-semibold text-pink-600 uppercase tracking-wide bg-pink-50/50">
                    FirstCry
                  </th>
                  <th className="text-center p-2 text-[9px] font-semibold text-rose-600 uppercase tracking-wide bg-rose-50/50">
                    Myntra
                  </th>
                  <th className="text-center p-2 text-[9px] font-semibold text-emerald-600 uppercase tracking-wide bg-emerald-50/50">
                    D2C Drogo
                  </th>
                  <th className="text-center p-2 text-[9px] font-semibold text-amber-600 uppercase tracking-wide bg-amber-50/50">
                    D2C Baybee
                  </th>
                  <th className="text-center p-2 text-[9px] font-semibold text-violet-600 uppercase tracking-wide bg-violet-50/50">
                    D2C Domestica
                  </th>
                  <th 
                    className="text-right p-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('days_cover')}
                  >
                    Cover {renderSortIcon('days_cover')}
                  </th>
                  <th className="text-center p-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    Health
                  </th>
                  <th className="text-center p-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedInventory.map((item, index) => (
                  <React.Fragment key={item.sku_code}>
                    <tr 
                      className={`hover:bg-gray-50 transition-colors cursor-pointer ${expandedRows.has(item.sku_code) ? 'bg-slate-50' : ''}`} 
                      data-testid={`inventory-row-${index}`}
                      onClick={() => toggleRowExpand(item.sku_code)}
                    >
                      <td className="p-3 text-center">
                        <button type="button" onClick={(e) => { e.stopPropagation(); toggleRowExpand(item.sku_code); }}
                          aria-expanded={expandedRows.has(item.sku_code)} aria-label={`${expandedRows.has(item.sku_code) ? 'Hide' : 'Show'} channel split for ${item.sku_code}`}
                          className="rounded p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${expandedRows.has(item.sku_code) ? 'rotate-180' : ''}`} aria-hidden="true" />
                        </button>
                      </td>
                      <td className="p-3">
                        <div className="font-mono text-[11px] font-medium text-black">{item.sku_code}</div>
                        <div className="text-[10px] text-gray-500 truncate max-w-[120px]">{item.product_name || '-'}</div>
                      </td>
                      <td className="p-3 text-right font-mono text-[13px] font-semibold">
                        <span className={availableStockClass(item.available_stock)}>
                          {item.available_stock}
                        </span>
                      </td>
                      {/* Bin Stock Cells - India Platforms */}
                      <td className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <BinStockCell 
                          value={item.bin_stock?.warehouse} 
                          platform="warehouse"
                          onEdit={(e) => openBinStockModal(item, e)}
                        />
                      </td>
                      <td className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <BinStockCell 
                          value={item.bin_stock?.amazon_seller} 
                          platform="amazon_seller"
                          recommendation={item.bin_recommendations?.amazon_seller}
                          onEdit={(e) => openBinStockModal(item, e)}
                        />
                      </td>
                      <td className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <BinStockCell 
                          value={item.bin_stock?.amazon_retail} 
                          platform="amazon_retail"
                          recommendation={item.bin_recommendations?.amazon_retail}
                          onEdit={(e) => openBinStockModal(item, e)}
                        />
                      </td>
                      <td className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <BinStockCell 
                          value={item.bin_stock?.flipkart} 
                          platform="flipkart"
                          recommendation={item.bin_recommendations?.flipkart}
                          onEdit={(e) => openBinStockModal(item, e)}
                        />
                      </td>
                      <td className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <BinStockCell 
                          value={item.bin_stock?.firstcry} 
                          platform="firstcry"
                          recommendation={item.bin_recommendations?.firstcry}
                          onEdit={(e) => openBinStockModal(item, e)}
                        />
                      </td>
                      <td className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <BinStockCell 
                          value={item.bin_stock?.myntra} 
                          platform="myntra"
                          recommendation={item.bin_recommendations?.myntra}
                          onEdit={(e) => openBinStockModal(item, e)}
                        />
                      </td>
                      <td className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <BinStockCell 
                          value={item.bin_stock?.d2c_drogo} 
                          platform="d2c_drogo"
                          recommendation={item.bin_recommendations?.d2c_drogo}
                          onEdit={(e) => openBinStockModal(item, e)}
                        />
                      </td>
                      <td className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <BinStockCell 
                          value={item.bin_stock?.d2c_baybee} 
                          platform="d2c_baybee"
                          recommendation={item.bin_recommendations?.d2c_baybee}
                          onEdit={(e) => openBinStockModal(item, e)}
                        />
                      </td>
                      <td className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <BinStockCell 
                          value={item.bin_stock?.d2c_domestica} 
                          platform="d2c_domestica"
                          recommendation={item.bin_recommendations?.d2c_domestica}
                          onEdit={(e) => openBinStockModal(item, e)}
                        />
                      </td>
                      <td className="p-3 text-right font-mono text-[12px]">
                        <span className={daysCoverClass(item.days_cover, item.available_stock, item.daily_avg_sales)} title={coverTitle(item)} data-testid={`cover-${item.sku_code}`}>
                          {formatDaysOfStock(item.days_cover, { ...coverOptions(item), short: true })}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <StockStatusBadge status={item.health} map={INVENTORY_HEALTH} className="text-[10px]" />
                      </td>
                      <td className="p-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={(e) => openTransferModal(item, e)}
                            className="h-7 w-7 rounded-lg hover:bg-blue-50"
                            title="Transfer stock"
                            aria-label={`Transfer stock for ${item.sku_code}`}
                          >
                            <ArrowRightLeft className="h-3.5 w-3.5 text-blue-500" aria-hidden="true" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); openEditDialog(item); }}
                            className="h-7 w-7 rounded-lg hover:bg-gray-100"
                            aria-label={`Edit inventory for ${item.sku_code}`}
                            title="Edit inventory"
                            data-testid={`edit-inventory-${index}`}
                          >
                            <Edit2 className="h-3.5 w-3.5 text-gray-500" aria-hidden="true" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {/* Channel Split Expanded Row */}
                    {expandedRows.has(item.sku_code) && (
                      <ChannelSplitRow item={item} />
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {paginatedInventory.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                {hasActiveFilters ? 'No inventory records match your filters' : 'No inventory records found'}
              </div>
            )}
            
            {/* Pagination Controls */}
            {filteredInventory.length > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-between px-5 py-4 border-t bg-gray-50">
                <div className="text-[13px] text-gray-500">
                  Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredInventory.length)} of {filteredInventory.length} items
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="h-8 px-3 text-[13px]"
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className={`h-8 w-8 p-0 text-[13px] ${currentPage === pageNum ? 'bg-[#007AFF]' : ''}`}
                          data-testid={`page-${pageNum}-btn`}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="h-8 px-3 text-[13px]"
                    data-testid="next-page-btn"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-[17px] font-semibold">Update Inventory - {formData.sku_code}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[13px] font-medium text-gray-700">Opening Stock</Label>
              <Input
                type="number"
                value={formData.opening_stock}
                onChange={(e) => setFormData({...formData, opening_stock: parseInt(e.target.value) || 0})}
                className="rounded-lg"
                data-testid="opening-stock-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[13px] font-medium text-gray-700">Purchased Stock</Label>
              <Input
                type="number"
                value={formData.incoming_stock}
                onChange={(e) => setFormData({...formData, incoming_stock: parseInt(e.target.value) || 0})}
                className="rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[13px] font-medium text-gray-700">Adjustment</Label>
              <Input
                type="number"
                value={formData.adjustment}
                onChange={(e) => setFormData({...formData, adjustment: parseInt(e.target.value) || 0})}
                className="rounded-lg"
              />
              <p className="text-[11px] text-gray-500">Use negative values for stock reduction</p>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">
                Cancel
              </Button>
              <Button type="submit" className="bg-[#007AFF] hover:bg-[#0066CC] rounded-lg" data-testid="save-inventory-button">
                Update
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkUploadOpen} onOpenChange={setBulkUploadOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[17px] font-semibold">Bulk Inventory Update</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5">
            <div className="p-4 bg-blue-50 rounded-xl">
              <p className="text-[13px] text-blue-700 leading-relaxed">
                Upload an Excel file to bulk update opening stock for multiple SKUs at once. 
                Download the template first to see the required format.
              </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <FileSpreadsheet className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-[14px] font-medium text-black">Download Template</p>
                  <p className="text-[12px] text-gray-500">Excel file with current SKUs</p>
                </div>
              </div>
              <Button 
                onClick={downloadTemplate}
                variant="outline"
                className="h-9 px-4 rounded-lg text-[13px]"
                data-testid="download-template-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>

            <div
              onDragOver={handleBulkDragOver}
              onDragEnter={handleBulkDragOver}
              onDragLeave={handleBulkDragLeave}
              onDrop={handleBulkDrop}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                uploading || bulkDragActive ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid="bulk-upload-dropzone"
            >
              <input
                type="file"
                ref={fileInputRef}
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="file-input"
              />
              
              {uploading ? (
                <div className="space-y-2">
                  <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="text-[14px] text-blue-600 font-medium">{importPreviewOpen ? 'Importing...' : 'Checking file...'}</p>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-[14px] font-medium text-gray-700 mb-1">
                    {bulkDragActive ? 'Release to check this file' : 'Drop your Excel file here'}
                  </p>
                  <p className="text-[12px] text-gray-500 mb-4">or</p>
                  <Button 
                    onClick={() => fileInputRef.current?.click()}
                    className="h-9 px-4 bg-[#007AFF] hover:bg-[#0066CC] rounded-lg text-[13px]"
                    data-testid="select-file-btn"
                  >
                    Select File
                  </Button>
                </>
              )}
            </div>

            {uploadResult && <UploadResultPanel result={uploadResult} source="stock" />}
          </div>

          <div className="flex justify-end pt-2">
            <Button 
              variant="outline" 
              onClick={() => { setBulkUploadOpen(false); setUploadResult(null); }}
              className="rounded-lg"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bin Stock Edit Modal */}
      <Dialog open={binStockModalOpen} onOpenChange={setBinStockModalOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[17px] font-semibold flex items-center gap-2">
              <Warehouse className="h-5 w-5 text-blue-500" />
              Edit Bin Stock - {binStockEditItem?.sku_code}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBinStockSubmit} className="space-y-4">
            <p className="text-[12px] text-gray-500 -mt-2">{binStockEditItem?.product_name}</p>
            
            {/* Warehouse - Main Stock */}
            <div className="space-y-1.5 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <Label className="text-[12px] font-medium text-blue-700 flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-blue-500" /> Warehouse (Main Stock)
              </Label>
              <Input
                type="number"
                min="0"
                value={binStockForm.warehouse}
                onChange={(e) => setBinStockForm({...binStockForm, warehouse: parseInt(e.target.value) || 0})}
                className="rounded-lg h-9 bg-white"
                data-testid="bin-warehouse-input"
              />
              <p className="text-[10px] text-blue-600">Central pool for Retail POs and platform refills</p>
            </div>
            
            {/* Amazon Section */}
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-100">
              <p className="text-[11px] font-semibold text-orange-700 mb-2">Amazon</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-medium text-amber-700 flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-amber-500" /> Amazon Vendor (at Amazon WH)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={binStockForm.amazon_retail}
                    onChange={(e) => setBinStockForm({...binStockForm, amazon_retail: parseInt(e.target.value) || 0})}
                    className={`rounded-lg h-9 bg-white text-[12px] ${amazonVendorLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={amazonVendorLocked}
                    readOnly={amazonVendorLocked}
                    data-testid="bin-amazon-retail-input"
                  />
                  <p className="text-[9px] text-amber-600">
                    {amazonVendorLocked ? '🔒 Via PO dispatch only' : 'Vendor/Retail stock at Amazon WH'}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-medium text-orange-600 flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-orange-400" /> Amazon Seller
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={binStockForm.amazon_seller}
                    onChange={(e) => setBinStockForm({...binStockForm, amazon_seller: parseInt(e.target.value) || 0})}
                    className="rounded-lg h-9 bg-white text-[12px]"
                    data-testid="bin-amazon-seller-input"
                  />
                  <p className="text-[11px] text-orange-700">You sell & ship</p>
                </div>
              </div>
              {amazonVendorLocked && (
                <p className="text-[9px] text-orange-600 mt-2 bg-orange-100 rounded px-2 py-1">
                  ℹ️ Amazon Vendor stock is locked. Use Retail PO dispatch to add stock.
                </p>
              )}
            </div>
            
            {/* Other Platforms */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] font-medium text-blue-700 flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500" /> Flipkart
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={binStockForm.flipkart}
                  onChange={(e) => setBinStockForm({...binStockForm, flipkart: parseInt(e.target.value) || 0})}
                  className="rounded-lg h-9"
                  data-testid="bin-flipkart-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-medium text-pink-700 flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-pink-500" /> FirstCry
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={binStockForm.firstcry}
                  onChange={(e) => setBinStockForm({...binStockForm, firstcry: parseInt(e.target.value) || 0})}
                  className="rounded-lg h-9"
                  data-testid="bin-firstcry-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-medium text-rose-700 flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-rose-500" /> Myntra
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={binStockForm.myntra}
                  onChange={(e) => setBinStockForm({...binStockForm, myntra: parseInt(e.target.value) || 0})}
                  className="rounded-lg h-9"
                  data-testid="bin-myntra-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-medium text-emerald-700 flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" /> D2C Drogo
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={binStockForm.d2c_drogo}
                  onChange={(e) => setBinStockForm({...binStockForm, d2c_drogo: parseInt(e.target.value) || 0})}
                  className="rounded-lg h-9"
                  data-testid="bin-d2c-drogo-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-medium text-amber-700 flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-amber-500" /> D2C Baybee
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={binStockForm.d2c_baybee}
                  onChange={(e) => setBinStockForm({...binStockForm, d2c_baybee: parseInt(e.target.value) || 0})}
                  className="rounded-lg h-9"
                  data-testid="bin-d2c-baybee-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-medium text-violet-700 flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-violet-500" /> D2C Domestica
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={binStockForm.d2c_domestica}
                  onChange={(e) => setBinStockForm({...binStockForm, d2c_domestica: parseInt(e.target.value) || 0})}
                  className="rounded-lg h-9"
                  data-testid="bin-d2c-domestica-input"
                />
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between text-[12px]">
                <span className="text-gray-600">Total Bin Stock:</span>
                <span className="font-semibold text-gray-900">
                  {binStockForm.warehouse + binStockForm.amazon_seller + binStockForm.amazon_retail + binStockForm.flipkart + binStockForm.firstcry + binStockForm.myntra + binStockForm.d2c_drogo + binStockForm.d2c_baybee + binStockForm.d2c_domestica} units
                </span>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setBinStockModalOpen(false)} className="rounded-lg">
                Cancel
              </Button>
              <Button type="submit" className="bg-[#007AFF] hover:bg-[#0066CC] rounded-lg" data-testid="save-bin-stock-btn">
                Save Bin Stock
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock Transfer Modal */}
      <Dialog open={transferModalOpen} onOpenChange={(open) => { if (!transferring) setTransferModalOpen(open); }}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[17px] font-semibold flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-blue-500" />
              Transfer Stock
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTransfer} className="space-y-4">
            <p className="text-[12px] text-gray-500 -mt-2">SKU: {transferForm.sku_code}{transferItem?.product_name ? ` · ${transferItem.product_name}` : ''}</p>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="transfer-from" className="text-[12px] font-medium text-gray-700">From bin</Label>
                <Select value={transferForm.from_bin} onValueChange={(v) => setTransferForm({ ...transferForm, from_bin: v })}>
                  <SelectTrigger id="transfer-from" className="h-9 rounded-lg" data-testid="transfer-from-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BIN_KEYS.map(key => (
                      <SelectItem key={key} value={key}>{binName(key)} · {Number(transferItem?.bin_stock?.[key] || 0).toLocaleString('en-IN')} units</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-center">
                <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="transfer-to" className="text-[12px] font-medium text-gray-700">To bin</Label>
                <Select value={transferForm.to_bin} onValueChange={(v) => setTransferForm({ ...transferForm, to_bin: v })}>
                  <SelectTrigger id="transfer-to" className="h-9 rounded-lg" data-testid="transfer-to-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BIN_KEYS.map(key => (
                      <SelectItem key={key} value={key}>{binName(key)} · {Number(transferItem?.bin_stock?.[key] || 0).toLocaleString('en-IN')} units</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="transfer-quantity" className="text-[12px] font-medium text-gray-700">Quantity</Label>
                <Input
                  id="transfer-quantity"
                  type="number"
                  min="1"
                  max={transferSourceQty > 0 ? transferSourceQty : undefined}
                  value={transferForm.quantity}
                  onChange={(e) => setTransferForm({ ...transferForm, quantity: parseInt(e.target.value, 10) || 0 })}
                  className="rounded-lg h-9"
                  placeholder="Enter quantity to transfer"
                  aria-describedby="transfer-hint"
                  aria-invalid={Boolean(transferBlocker) && transferForm.quantity > 0 ? true : undefined}
                  data-testid="transfer-quantity-input"
                />
                <p id="transfer-hint" className={`text-[11px] ${transferBlocker ? 'text-amber-700' : 'text-gray-500'}`} data-testid="transfer-hint">
                  {transferBlocker || `${binName(transferForm.from_bin)} has ${transferSourceQty.toLocaleString('en-IN')} units; ${binName(transferForm.to_bin)} will have ${(Number(transferItem?.bin_stock?.[transferForm.to_bin] || 0) + transferForm.quantity).toLocaleString('en-IN')} after the move.`}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setTransferModalOpen(false)} disabled={transferring} className="rounded-lg">
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 rounded-lg"
                disabled={Boolean(transferBlocker) || transferring}
                data-testid="confirm-transfer-btn"
              >
                {transferring ? 'Transferring…' : 'Transfer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bin Stock Bulk Upload Modal */}
      <Dialog open={binStockBulkOpen} onOpenChange={setBinStockBulkOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[17px] font-semibold flex items-center gap-2">
              <Warehouse className="h-5 w-5 text-blue-500" />
              Bulk Bin Stock Import
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[13px] text-gray-600">
              Import bin stock for multiple SKUs using an Excel file. Download the template first, fill in the quantities, then upload.
            </p>
            
            <div className="bg-blue-50 rounded-lg p-3 text-[12px] text-blue-700">
              <p className="font-medium mb-1">Template Columns:</p>
              <p>SKU Code | Warehouse | Amz Seller | Amz Vendor | Flipkart | FirstCry | Myntra | D2C Drogo | D2C Baybee | D2C Domestica</p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={downloadBinStockTemplate}
                className="flex-1 rounded-lg"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
              <Button 
                onClick={() => binStockFileInputRef.current?.click()}
                disabled={binStockUploading}
                className="flex-1 bg-[#007AFF] hover:bg-[#0066CC] rounded-lg"
              >
                {binStockUploading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload File
                  </>
                )}
              </Button>
              <input
                ref={binStockFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleBinStockFileUpload}
                className="hidden"
                data-testid="bin-stock-file-input"
              />
            </div>
            
            {binStockUploadResult && <UploadResultPanel result={binStockUploadResult} source="bin-stock" />}
          </div>
          
          <div className="flex justify-end pt-2">
            <Button 
              variant="outline" 
              onClick={() => { setBinStockBulkOpen(false); setBinStockUploadResult(null); }}
              className="rounded-lg"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ImportPreviewDialog
        open={importPreviewOpen}
        onOpenChange={setImportPreviewOpen}
        title={importPreview ? importConfig[importPreview.kind].title : 'Review import'}
        fileName={importPreview?.fileName}
        preview={importPreview?.preview}
        entityLabel={importPreview ? importConfig[importPreview.kind].entityLabel : 'rows'}
        confirmLabel="Confirm import"
        onConfirm={confirmImport}
        testId="inventory-import-preview"
      />
    </div>
  );
};
